/**
 * Dữ liệu mẫu cho cấu hình bảo mật theo tầng (ADR-0059).
 *
 * `MOCK_DANH_MUC` chép **đúng** `libs/platform-common/src/main/resources/platform/
 * settings-registry.yaml` (29 khoá, cùng kiểu, biên, hướng siết, đơn vị). Sửa
 * YAML thì sửa ở đây; khoá dịch `caiDat.<key>.nhan/moTa` khai ở vi.ts và en.ts.
 *
 * Mỗi nhánh xấu đều có ít nhất một bản ghi: loại thông báo chưa có người nhận,
 * dòng allowlist đã hết hạn, đồng bộ mật khẩu đang chờ, thông báo gửi lỗi.
 */
import type {
  BaoCaoIpMonitor,
  CanhBaoAudit,
  DongChanIp,
  MaGoi,
  PhienBanCaiDat,
  SettingDefinition,
  TaiKhoanKhoaTam,
  ThongBao,
  TrangThaiDongBo,
} from '../modules/bao-mat/types';

type Def = Omit<SettingDefinition, 'label_key' | 'description_key'>;

const PA = 'PLATFORM_ADMIN' as const;
const TA = 'TENANT_ADMIN' as const;
const CHI_PA_NEN_TANG = { PLATFORM: [PA] };

const route = (severity: 'INFO' | 'WARNING' | 'CRITICAL') => ({
  channels: ['EMAIL', 'PORTAL'],
  recipients: [],
  min_severity: severity,
});

const DINH_NGHIA: Def[] = [
  {
    key: 'auth.mfa_required',
    group: 'auth',
    type: 'boolean',
    default: false,
    scopes: ['PLATFORM', 'PLAN', 'TENANT'],
    editable_by: { PLATFORM: [PA], PLAN: [PA], TENANT: [PA, TA] },
    direction: 'TRUE_IS_STRICTER',
    enforced_at: 'keycloak',
  },
  {
    // ADR-0099: tắt thì nhận email cá nhân và ép MFA cả tổ chức
    key: 'auth.chi_email_domain_xac_minh',
    group: 'auth',
    type: 'boolean',
    default: true,
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA, TA] },
    direction: 'ANY',
    enforced_at: 'gateway',
  },
  {
    key: 'network.ip_allowlist',
    group: 'network',
    type: 'cidr_list',
    default: [],
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA, TA] },
    bounds: { max_items: 200, forbid_catch_all: true },
    direction: 'ANY',
    enforced_at: 'gateway',
  },
  {
    // ADR-0090 mục 2: ngoài dải tin cậy thì phiên phải đã qua OTP
    key: 'network.mfa_ngoai_dai',
    group: 'network',
    type: 'boolean',
    default: false,
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA, TA] },
    direction: 'TRUE_IS_STRICTER',
    enforced_at: 'gateway',
  },
  {
    // ADR-0090 mục 3.2: chặn tạm IP đăng nhập sai dồn dập; 0 là tắt
    key: 'network.auto_block_minutes',
    group: 'network',
    type: 'integer',
    default: 15,
    scopes: ['PLATFORM', 'TENANT'],
    editable_by: { PLATFORM: [PA], TENANT: [PA, TA] },
    bounds: { min: 0, max: 1440 },
    direction: 'HIGHER_IS_STRICTER',
    enforced_at: 'service',
    unit: 'phút',
  },
  {
    key: 'audit.allow_sample_rate',
    group: 'audit',
    type: 'number',
    default: 0.1,
    scopes: ['PLATFORM', 'PLAN', 'TENANT'],
    editable_by: { PLATFORM: [PA], PLAN: [PA], TENANT: [PA] },
    bounds: { min: 0.01, max: 1 },
    direction: 'ANY',
    enforced_at: 'gateway',
  },
  {
    key: 'audit.read_sample_rate',
    group: 'audit',
    type: 'number',
    default: 0.1,
    scopes: ['PLATFORM', 'PLAN', 'TENANT'],
    editable_by: { PLATFORM: [PA], PLAN: [PA], TENANT: [PA] },
    bounds: { min: 0.01, max: 1 },
    direction: 'ANY',
    enforced_at: 'service',
  },
  {
    key: 'audit.login_error_burst_per_ip',
    group: 'audit',
    type: 'integer',
    default: 20,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 5, max: 1000 },
    direction: 'ANY',
    enforced_at: 'keycloak',
    unit: 'sự kiện/s',
  },
  {
    key: 'audit.alert_rules',
    group: 'audit',
    type: 'alert_rule_list',
    default: [],
    scopes: ['PLATFORM', 'PLAN', 'TENANT'],
    editable_by: { PLATFORM: [PA], PLAN: [PA], TENANT: [PA, TA] },
    bounds: {
      max_items: 20,
      fields: {
        threshold: { min: 2, max: 10000 },
        window_seconds: { min: 10, max: 86400 },
        cooldown_seconds: { min: 60, max: 86400 },
        actions: { max_items: 20 },
        error_codes: { max_items: 20 },
      },
    },
    direction: 'ANY',
    enforced_at: 'service',
  },
  {
    key: 'log.debug_until',
    group: 'log',
    type: 'timestamp',
    default: null,
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA] },
    bounds: { max_future_minutes: 60 },
    direction: 'ANY',
    enforced_at: 'service',
  },
  // ADR-0078: tổ chức đặt chặt hơn, mức nền tảng là sàn
  {
    key: 'auth.password_min_length',
    group: 'auth',
    type: 'integer',
    default: 10,
    scopes: ['PLATFORM', 'TENANT'],
    editable_by: { PLATFORM: [PA], TENANT: [PA, TA] },
    bounds: { min: 10, max: 64 },
    direction: 'HIGHER_IS_STRICTER',
    inherited_is_floor: true,
    enforced_at: 'keycloak',
    unit: 'ký tự',
  },
  {
    key: 'auth.password_max_length',
    group: 'auth',
    type: 'integer',
    default: 128,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 64, max: 256 },
    direction: 'ANY',
    enforced_at: 'keycloak',
    unit: 'ký tự',
  },
  {
    key: 'auth.password_history',
    group: 'auth',
    type: 'integer',
    default: 5,
    scopes: ['PLATFORM', 'TENANT'],
    editable_by: { PLATFORM: [PA], TENANT: [PA, TA] },
    bounds: { min: 0, max: 24 },
    direction: 'HIGHER_IS_STRICTER',
    inherited_is_floor: true,
    enforced_at: 'keycloak',
  },
  {
    key: 'auth.bruteforce_max_failures',
    group: 'auth',
    type: 'integer',
    default: 5,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 3, max: 5 },
    direction: 'LOWER_IS_STRICTER',
    enforced_at: 'keycloak',
    unit: 'lần',
  },
  {
    key: 'auth.bruteforce_lock_minutes',
    group: 'auth',
    type: 'integer',
    default: 15,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 15, max: 1440 },
    direction: 'HIGHER_IS_STRICTER',
    enforced_at: 'keycloak',
    unit: 'phút',
  },
  {
    key: 'auth.password_blacklist_enabled',
    group: 'auth',
    type: 'boolean',
    default: true,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    direction: 'TRUE_IS_STRICTER',
    enforced_at: 'keycloak',
  },
  {
    key: 'notify.security_critical',
    group: 'notify',
    type: 'notify_route',
    default: route('CRITICAL'),
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { max_items: 50 },
    direction: 'ANY',
    enforced_at: 'notification',
  },
  {
    key: 'notify.ops_warning',
    group: 'notify',
    type: 'notify_route',
    default: route('WARNING'),
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { max_items: 50 },
    direction: 'ANY',
    enforced_at: 'notification',
  },
  {
    key: 'notify.tenant_security',
    group: 'notify',
    type: 'notify_route',
    default: route('WARNING'),
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA, TA] },
    bounds: { max_items: 50 },
    direction: 'ANY',
    enforced_at: 'notification',
  },
  {
    key: 'notify.audit_alert',
    group: 'notify',
    type: 'notify_route',
    default: route('WARNING'),
    scopes: ['TENANT'],
    editable_by: { TENANT: [PA, TA] },
    bounds: { max_items: 50 },
    direction: 'ANY',
    enforced_at: 'notification',
  },
  {
    key: 'notify.license_expiry',
    group: 'notify',
    type: 'notify_route',
    default: route('INFO'),
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { max_items: 50 },
    direction: 'ANY',
    enforced_at: 'notification',
  },
  {
    key: 'contact.types',
    group: 'contact',
    type: 'string_list',
    default: ['ADMIN', 'CONTRACT_OWNER', 'TECHNICAL', 'SECURITY', 'BILLING'],
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { max_items: 20, item_pattern: '^[A-Z_]{2,30}$' },
    direction: 'ANY',
    enforced_at: 'service',
  },
  {
    key: 'license.default_term_months',
    group: 'license',
    type: 'integer',
    default: 12,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 1, max: 60 },
    direction: 'ANY',
    enforced_at: 'license',
    unit: 'tháng',
  },
  {
    key: 'license.grace_days',
    group: 'license',
    type: 'integer',
    default: 7,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 0, max: 7 },
    direction: 'ANY',
    enforced_at: 'license',
    unit: 'ngày',
  },
  {
    key: 'license.renewal_window_days',
    group: 'license',
    type: 'integer',
    default: 45,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 7, max: 180 },
    direction: 'ANY',
    enforced_at: 'license',
    unit: 'ngày',
  },
  {
    key: 'license.notify_days',
    group: 'license',
    type: 'integer_list',
    default: [45, 30, 7, 1],
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 0, max: 365, max_items: 10 },
    direction: 'ANY',
    enforced_at: 'license',
    unit: 'ngày',
  },
  {
    key: 'license.approval.max_term_months',
    group: 'license',
    type: 'integer',
    default: 12,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { min: 1, max: 12 },
    direction: 'LOWER_IS_STRICTER',
    enforced_at: 'license',
    unit: 'tháng',
  },
  {
    key: 'license.approval.over_plan_quota',
    group: 'license',
    type: 'boolean',
    default: true,
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    direction: 'TRUE_IS_STRICTER',
    enforced_at: 'license',
  },
  {
    key: 'license.approval.special_features',
    group: 'license',
    type: 'string_list',
    default: ['*'],
    scopes: ['PLATFORM'],
    editable_by: CHI_PA_NEN_TANG,
    bounds: { max_items: 100, item_pattern: String.raw`^(\*|[A-Za-z0-9_.:-]{1,100})$` },
    direction: 'ANY',
    enforced_at: 'license',
  },
];

export const MOCK_DANH_MUC: SettingDefinition[] = DINH_NGHIA.map((d) => ({
  ...d,
  label_key: `caiDat.${d.key}.nhan`,
  description_key: `caiDat.${d.key}.moTa`,
}));

/** Gói Control Plane của từng tenant mẫu, khoá theo `tenant_id` của `mock/tenant.ts`. */
export const MOCK_GOI_CUA_TENANT: Record<string, MaGoi> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000001': 'ENTERPRISE',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': 'PROFESSIONAL',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000003': 'STANDARD',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000004': 'STANDARD',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000005': 'ENTERPRISE',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000006': 'TRIAL',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000009': 'STANDARD',
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000011': 'ENTERPRISE',
};

/** IP mà Gateway thấy cho người đang dùng giao diện mẫu. */
export const MOCK_IP_NGUOI_GOI = '113.161.42.18';

export interface TaiLieuCaiDat {
  settings: Record<string, unknown>;
  version: number;
  updated_at: string;
  updated_by: string | null;
}

export const MOCK_CAI_DAT_NEN_TANG: TaiLieuCaiDat = {
  settings: {
    // `notify.ops_warning` cố ý **không** có người nhận: màn hình phải báo.
    'notify.security_critical': {
      channels: ['EMAIL', 'CHAT', 'PORTAL'],
      recipients: ['secops@foxai.vn'],
      min_severity: 'CRITICAL',
    },
    'notify.license_expiry': {
      channels: ['EMAIL', 'PORTAL'],
      recipients: ['sales-ops@foxai.vn', 'cskh@foxai.vn'],
      min_severity: 'INFO',
    },
    'auth.password_min_length': 12,
  },
  version: 7,
  updated_at: '2026-10-17T09:12:00+07:00',
  updated_by: 'an.nguyen@foxai.vn',
};

export const MOCK_CAI_DAT_GOI: Record<MaGoi, TaiLieuCaiDat> = {
  TRIAL: { settings: {}, version: 0, updated_at: '2026-10-01T08:00:00+07:00', updated_by: null },
  STANDARD: { settings: {}, version: 0, updated_at: '2026-10-01T08:00:00+07:00', updated_by: null },
  PROFESSIONAL: {
    settings: { 'audit.allow_sample_rate': 0.2 },
    version: 2,
    updated_at: '2026-10-10T14:30:00+07:00',
    updated_by: 'an.nguyen@foxai.vn',
  },
  ENTERPRISE: {
    settings: { 'auth.mfa_required': true },
    version: 3,
    updated_at: '2026-10-12T10:05:00+07:00',
    updated_by: 'an.nguyen@foxai.vn',
  },
};

export const MOCK_CAI_DAT_TENANT: Record<string, TaiLieuCaiDat> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': {
    settings: {
      'network.ip_allowlist': [
        { cidr: '113.161.42.0/24', note: 'Văn phòng Hà Nội', expires_at: null },
        { cidr: '14.232.0.0/16', note: 'VPN công ty', expires_at: null },
        { cidr: '203.0.113.7/32', note: 'Máy chủ tích hợp kế toán', expires_at: null },
      ],
      'auth.password_history': 8,
      // Tổ chức đặt dài hơn mức sàn 12 của nền tảng (ADR-0078)
      'auth.password_min_length': 14,
      'audit.alert_rules': [
        {
          id: 'deny-burst',
          name: 'Nhiều lần bị từ chối',
          enabled: true,
          decision: 'DENY',
          actions: [],
          resource_prefix: null,
          sources: [],
          error_codes: [],
          group_by: 'user',
          threshold: 20,
          window_seconds: 300,
          cooldown_seconds: 900,
          severity: 'WARNING',
        },
        {
          id: 'xuat-du-lieu-dem',
          name: 'Xuất dữ liệu hàng loạt',
          enabled: false,
          decision: 'ALLOW',
          actions: ['ekyc:profile:export', 'ocr.*.export'],
          resource_prefix: '/api/v1/',
          sources: ['gateway', 'service'],
          error_codes: [],
          group_by: 'user',
          threshold: 50,
          window_seconds: 3600,
          cooldown_seconds: 3600,
          severity: 'CRITICAL',
        },
        {
          id: 'dang-nhap-sai-theo-ip',
          name: 'Đăng nhập sai từ một IP',
          enabled: true,
          decision: 'DENY',
          actions: ['LOGIN'],
          resource_prefix: null,
          sources: ['keycloak'],
          error_codes: ['INVALID_CREDENTIALS'],
          group_by: 'ip',
          threshold: 30,
          window_seconds: 600,
          cooldown_seconds: 1800,
          severity: 'INFO',
        },
      ],
    },
    version: 5,
    updated_at: '2026-10-22T10:05:00+07:00',
    updated_by: 'ha.pham@khachhang.vn',
  },
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000001': {
    settings: { 'audit.allow_sample_rate': 0.5 },
    version: 1,
    updated_at: '2026-09-02T11:00:00+07:00',
    updated_by: 'an.nguyen@foxai.vn',
  },
};

/** Phiên bản cũ kèm **toàn bộ** settings sau thay đổi — như `security_policy_revision`. */
export interface PhienBanMau extends PhienBanCaiDat {
  settings: Record<string, unknown>;
}

export const MOCK_LICH_SU_TENANT: Record<string, PhienBanMau[]> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': [
    {
      version: 5,
      changed_by: 'ha.pham@khachhang.vn',
      changed_at: '2026-10-22T10:05:00+07:00',
      reason: 'Áp quy chế an toàn thông tin nội bộ: mật khẩu tối thiểu 14 ký tự',
      changes: [{ key: 'auth.password_min_length', before: null, after: 14 }],
      settings: MOCK_CAI_DAT_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000002']!.settings,
    },
    {
      version: 4,
      changed_by: 'ha.pham@khachhang.vn',
      changed_at: '2026-10-16T16:40:00+07:00',
      reason: 'Không cho dùng lại 8 mật khẩu gần nhất theo quy chế nội bộ',
      changes: [{ key: 'auth.password_history', before: null, after: 8 }],
      settings: Object.fromEntries(
        Object.entries(MOCK_CAI_DAT_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000002']!.settings).filter(
          ([k]) => k !== 'auth.password_min_length',
        ),
      ),
    },
    {
      version: 3,
      changed_by: 'ha.pham@khachhang.vn',
      changed_at: '2026-10-12T09:15:00+07:00',
      reason: 'Chặn tạm lâu hơn khi IP đăng nhập sai dồn dập',
      changes: [{ key: 'network.auto_block_minutes', before: null, after: 60 }],
      settings: {
        'network.ip_allowlist': (MOCK_CAI_DAT_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000002']!.settings['network.ip_allowlist']),
        'network.auto_block_minutes': 60,
      },
    },
    {
      version: 2,
      changed_by: 'ha.pham@khachhang.vn',
      changed_at: '2026-10-08T10:02:00+07:00',
      reason: null,
      changes: [
        {
          key: 'network.ip_allowlist',
          before: [],
          after: MOCK_CAI_DAT_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000002']!.settings['network.ip_allowlist'],
        },
      ],
      settings: {
        'network.ip_allowlist': MOCK_CAI_DAT_TENANT['0a7d3c5e-1f2b-4e6a-9c8d-000000000002']!.settings['network.ip_allowlist'],
      },
    },
    {
      version: 1,
      changed_by: 'an.nguyen@foxai.vn',
      changed_at: '2026-09-20T08:30:00+07:00',
      reason: 'Khởi tạo khi onboarding',
      changes: [{ key: 'auth.mfa_required', before: null, after: false }],
      settings: { 'auth.mfa_required': false },
    },
  ],
};

export const MOCK_IP_MONITOR: Record<string, BaoCaoIpMonitor['items']> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': [
    {
      ip: '171.244.18.90',
      count: 412,
      last_seen_at: '2026-10-19T08:52:00+07:00',
      user_ids: ['u-201', 'u-202', 'u-207'],
    },
    { ip: '42.118.7.3', count: 37, last_seen_at: '2026-10-18T21:14:00+07:00', user_ids: ['u-215'] },
    {
      ip: '2402:800:6371:1a2b::17',
      count: 6,
      last_seen_at: '2026-10-17T07:40:00+07:00',
      user_ids: ['u-203'],
    },
  ],
};

/**
 * Người đang bị khoá tạm do đăng nhập sai (`GET …/locked-users`, ADR-0059 mục
 * 5.3), xếp theo **khúc tra** như user-service: mỗi phần tử là kết quả một khúc
 * hồ sơ. Khúc rỗng giữa chừng diễn đúng trường hợp "trang rỗng mà vẫn còn
 * `next_cursor`". Minh Long có người ở khúc 1 và 3; FoxAI khúc đầu rỗng.
 */
export const MOCK_KHOA_TAM: Record<string, TaiKhoanKhoaTam[][]> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': [
    [
      {
        user_id: '7c1e2a40-5b3d-4f60-8a91-000000000201',
        email: 'lan.pham@minhlong.vn',
        full_name: 'Phạm Thị Lan',
        num_failures: 5,
        last_failure: '2026-10-21T08:41:00+07:00',
        last_ip_failure: '171.244.18.90',
      },
      {
        user_id: '7c1e2a40-5b3d-4f60-8a91-000000000207',
        email: null,
        full_name: 'Đỗ Văn Khoa',
        num_failures: 7,
        last_failure: '2026-10-21T07:12:00+07:00',
        last_ip_failure: '2402:800:6371:1c2a::14',
      },
    ],
    [],
    [
      {
        user_id: '7c1e2a40-5b3d-4f60-8a91-000000000233',
        email: 'quang.vu@minhlong.vn',
        full_name: 'Vũ Minh Quang',
        num_failures: 5,
        last_failure: '2026-10-20T22:05:00+07:00',
        last_ip_failure: null,
      },
    ],
  ],
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000001': [
    [],
    [
      {
        user_id: '7c1e2a40-5b3d-4f60-8a91-000000000105',
        email: 'thu.nguyen@foxai.vn',
        full_name: 'Nguyễn Hoài Thu',
        num_failures: 6,
        last_failure: '2026-10-21T09:30:00+07:00',
        last_ip_failure: '113.161.72.4',
      },
    ],
  ],
};

/**
 * Người có hồ sơ nhưng không còn tài khoản Keycloak thuộc tenant: mở khoá nhận
 * `404 USER_NOT_FOUND` như user-service, để soát lỗi trong hộp xác nhận.
 */
export const MOCK_KHOA_TAM_MAT_TAI_KHOAN = new Set(['7c1e2a40-5b3d-4f60-8a91-000000000207']);

const ML = '0a7d3c5e-1f2b-4e6a-9c8d-000000000002';

/**
 * Cảnh báo đã kích hoạt (`GET …/audit/alerts`), mới nhất trước. Đủ mọi mức và
 * hai trạng thái, đủ dài để "Tải thêm" chạy một lần (trang 20 dòng). Bản ghi
 * `al-404` không còn ở server: đánh dấu đã xem nhận `404 NOT_FOUND`.
 */
export const MOCK_CANH_BAO: Record<string, CanhBaoAudit[]> = {
  [ML]: Array.from({ length: 26 }, (_, i): CanhBaoAudit => {
    const kieu = i % 3;
    const ketThuc = Date.parse('2026-10-21T09:05:00+07:00') - i * 53 * 60_000;
    const cuaSo = kieu === 2 ? 600 : 300;
    const daXem = i >= 4 && i % 4 !== 1;
    return {
      id: i === 2 ? 'al-404' : `al-${String(i + 1).padStart(3, '0')}`,
      tenant_id: ML,
      rule_id: kieu === 2 ? 'dang-nhap-sai-theo-ip' : 'deny-burst',
      rule_name: kieu === 2 ? 'Đăng nhập sai từ một IP' : 'Nhiều lần bị từ chối',
      severity: kieu === 2 ? 'INFO' : i % 5 === 0 ? 'CRITICAL' : 'WARNING',
      group_by: kieu === 2 ? 'ip' : 'user',
      group_key: kieu === 2 ? '171.244.18.x' : `7c1e2a40-5b3d-4f60-8a91-0000000002${String(i % 7).padStart(2, '0')}`,
      // Danh bạ không còn người ở hai dòng này: giao diện phải hiện "Không rõ tên".
      group_name: kieu === 2 || i % 7 === 0 ? null : (['Phạm Thu Hà', 'Đỗ Hoài Nam', 'Hoàng Đức Anh'][i % 3] ?? null),
      event_count: 20 + ((i * 7) % 23),
      threshold: kieu === 2 ? 30 : 20,
      window_seconds: cuaSo,
      window_start: new Date(ketThuc - cuaSo * 1000).toISOString(),
      window_end: new Date(ketThuc).toISOString(),
      sample_event_ids: [],
      status: daXem ? 'ACKNOWLEDGED' : 'OPEN',
      acknowledged_at: daXem ? new Date(ketThuc + 20 * 60_000).toISOString() : null,
      acknowledged_by: daXem ? 'ha.pham@khachhang.vn' : null,
      created_at: new Date(ketThuc).toISOString(),
    };
  }),
};

/** Cảnh báo đã bị dọn ở server: ACK trả `404 NOT_FOUND`. */
export const MOCK_CANH_BAO_MAT = new Set(['al-404']);

export const MOCK_TRANG_THAI_DONG_BO: TrangThaiDongBo = 'PENDING';

/**
 * Blocklist của tenant (ADR-0090 mục 3), chỉ dòng còn hiệu lực như server trả. Hạn tính từ lúc nạp trang để dòng
 * tự động luôn còn vài phút khi soát bằng mắt. Minh Long có đủ ba kiểu: vĩnh viễn, có hạn, chặn tự động.
 */
const sauPhut = (phut: number) => new Date(Date.now() + phut * 60_000).toISOString();
const truocPhut = (phut: number) => new Date(Date.now() - phut * 60_000).toISOString();

export const MOCK_CHAN_TENANT: Record<string, DongChanIp[]> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': [
    {
      id: 'tb-003',
      cidr: '185.220.101.46/32',
      reason: 'Tự động chặn: 23 lần đăng nhập sai trong 300 giây',
      source: 'AUTO',
      expires_at: sauPhut(12),
      created_by: null,
      created_at: truocPhut(3),
    },
    {
      id: 'tb-002',
      cidr: '103.97.124.0/24',
      reason: 'Dải của nhà cung cấp cũ, chặn trong lúc rà soát hợp đồng bảo trì và thu hồi tài khoản dùng chung',
      source: 'MANUAL',
      expires_at: sauPhut(60 * 24 * 6 + 5 * 60),
      created_by: 'u-004',
      created_at: truocPhut(60 * 19),
    },
    {
      id: 'tb-001',
      cidr: '198.51.100.23/32',
      reason: 'Dò mật khẩu ngày 12/10',
      source: 'MANUAL',
      expires_at: null,
      created_by: 'u-004',
      created_at: '2026-10-12T09:15:00+07:00',
    },
  ],
};

export const MOCK_CHAN_NEN_TANG: DongChanIp[] = [
  {
    id: 'bl-001',
    cidr: '45.155.205.0/24',
    reason: 'Dò mật khẩu rải trên nhiều tenant (cảnh báo audit 14/10)',
    source: 'MANUAL',
    expires_at: null,
    created_by: 'ns-002',
    created_at: '2026-10-14T22:05:00+07:00',
  },
  {
    id: 'bl-002',
    cidr: '2a03:2880:f12f::/48',
    reason: 'Quét lỗ hổng tự động',
    source: 'MANUAL',
    expires_at: sauPhut(60 * 5 + 20),
    created_by: null,
    created_at: '2026-10-18T03:11:00+07:00',
  },
  {
    id: 'bl-003',
    cidr: '198.51.100.0/24',
    reason: 'Chặn tạm 24 giờ theo yêu cầu của đội vận hành sau đợt tấn công từ chối dịch vụ nhắm vào cổng đăng nhập',
    source: 'MANUAL',
    expires_at: sauPhut(60 * 23),
    created_by: 'ns-002',
    created_at: '2026-10-17T00:00:00+07:00',
  },
];

const tieuDe: Array<[string, ThongBao['severity'], string, ThongBao['status'], string | null]> = [
  ['notify.security_critical', 'CRITICAL', 'Dò mật khẩu rải từ 45.155.205.17 qua 38 tài khoản', 'SENT', null],
  ['notify.ops_warning', 'WARNING', 'Rate limit chuyển sang đếm cục bộ trên 2/3 instance Gateway', 'NO_RECIPIENT', null],
  ['notify.tenant_security', 'WARNING', 'Công ty TNHH Thương mại Minh Long: 412 lượt truy cập ngoài dải tin cậy (chỉ ghi nhận)', 'SENT', '0a7d3c5e-1f2b-4e6a-9c8d-000000000002'],
  ['notify.license_expiry', 'INFO', 'License của Tập đoàn Xây dựng Hòa Bình hết hạn sau 30 ngày', 'SENT', '0a7d3c5e-1f2b-4e6a-9c8d-000000000005'],
  ['notify.ops_warning', 'WARNING', 'Phân vùng audit tháng 01/2027 chưa được tạo', 'NO_RECIPIENT', null],
  ['notify.security_critical', 'CRITICAL', 'Chuỗi băm audit của tenant an-khang bị đứt ở bản ghi 88213', 'FAILED', '0a7d3c5e-1f2b-4e6a-9c8d-000000000004'],
];

/** 60 thông báo gần đây — đủ để "Tải thêm" chạy hai lần. */
export const MOCK_THONG_BAO: ThongBao[] = Array.from({ length: 60 }, (_, i) => {
  const [notify_key, severity, title, status, tenant_id] = tieuDe[i % tieuDe.length]!;
  const luc = new Date(Date.parse('2026-10-19T09:00:00+07:00') - i * 97 * 60_000).toISOString();
  return {
    id: `nt-${String(i + 1).padStart(3, '0')}`,
    notify_key,
    tenant_id,
    severity,
    title,
    body: title,
    recipients: status === 'NO_RECIPIENT' ? [] : ['secops@foxai.vn'],
    channels: ['EMAIL', 'PORTAL'],
    status,
    created_at: luc,
  };
});

/** Câu lỗi backend trả về (không dịch — ADR-UI-0010 mục 5). */
export const MOCK_CAU_LOI = {
  cauHinhSai: 'Cấu hình không hợp lệ.',
  cidrSai: 'CIDR không hợp lệ.',
  phienBanCu: (v: number) => `Phiên bản ${v} không còn là phiên bản hiện tại.`,
  khongCoPhienBan: (v: number) => `Không có phiên bản ${v}.`,
  daCoTrongBlocklist: (cidr: string) => `Dải ${cidr} đã có trong blocklist.`,
  // `loi.blocklist.chua-ip-cua-ban` của tenant-service (ADR-0098)
  chuaIpCuaBan: (cidr: string, ip: string) =>
    `Dải ${cidr} chứa địa chỉ IP bạn đang dùng (${ip}); chặn sẽ khoá chính bạn. Chọn dải hẹp hơn, hoặc thêm từ mạng khác.`,
  khoiPhuc: (v: number) => `Khôi phục phiên bản ${v}`,
  // `loi.canh-bao.khong-co` của audit-service.
  khongCoCanhBao: 'Không tìm thấy cảnh báo này. Tải lại danh sách cảnh báo rồi thử lại.',
  // `loi.khoa-tam.khong-co` của user-service.
  khongCoNguoiKhoaTam:
    'Không tìm thấy người dùng này trong tenant. Tải lại danh sách tài khoản đang bị khoá tạm rồi thử lại.',
};

/**
 * `detail` của lỗi theo trường như `platform/loi.properties` trả (đã dịch phía
 * backend). Mã ở đây là mã của `kiemGiaTri()` phía giao diện.
 */
export const MOCK_CHI_TIET_LOI: Record<string, string> = {
  kieu_sai: 'Giá trị sai kiểu.',
  khong_phai_so_nguyen: 'Giá trị phải là số nguyên.',
  nho_hon_min: 'Giá trị nhỏ hơn mức tối thiểu cho phép.',
  lon_hon_max: 'Giá trị vượt mức tối đa cho phép.',
  ngoai_danh_sach: 'Giá trị không nằm trong danh sách cho phép.',
  qua_so_dong: 'Danh sách có quá nhiều dòng.',
  qua_so_nguoi_nhan: 'Danh sách người nhận quá dài.',
  cidr_sai: 'CIDR không hợp lệ.',
  cidr_trung: 'CIDR bị trùng.',
  cidr_tat_ca: 'Không được chặn toàn bộ địa chỉ.',
  thoi_diem_qua_khu: 'Thời điểm đã qua.',
  thoi_diem_qua_xa: 'Thời điểm vượt quá giới hạn cho phép.',
  email_sai: 'Địa chỉ email không hợp lệ.',
  sai_dinh_dang: 'Giá trị sai định dạng.',
  thieu_kenh: 'Cần ít nhất một kênh gửi.',
  chuoi_rong: 'Không được để phần tử rỗng.',
  chi_duoc_siet: 'Tenant chỉ được siết chặt hơn mức kế thừa.',
  qua_so_quy_tac: 'Danh sách có quá nhiều quy tắc cảnh báo.',
  quy_tac_sai: 'Quy tắc cảnh báo không hợp lệ.',
  khongDuocSua: 'Tham số này không sửa được ở tầng này với vai trò của bạn.',
};
